/**
 * Calculate suggested reorder quantity based on inventory data, forecast period, and forecasting modifiers
 * TODO: This is a mock formula. In production, this data should come from a backend API
 * that uses actual sales history and forecasting algorithms to determine reorder quantities.
 */
import { TimePeriod, DEFAULT_FORECAST_PERIOD } from './time-periods';

/**
 * @typedef {import('../types').InventoryEntity} InventoryEntity
 * @typedef {import('../types').ForecastingModifiers} ForecastingModifiers
 * @typedef {import('../types').SuggestedReorderResult} SuggestedReorderResult
 */

/**
 * @param {InventoryEntity} entity - Inventory entity
 * @param {string} forecastPeriod - Selected forecast period
 * @param {ForecastingModifiers} modifiers - Modifiers to apply to the calculation
 * @param {number} [avgOrders] - Average monthly orders
 * @returns {SuggestedReorderResult} - Object containing original and modified suggested reorder quantities
 */
export const calculateSuggestedReorder = (entity, forecastPeriod, modifiers = {}, avgOrders = 0) => {
  const stock = entity.stock ?? 0;
  const reorderThreshold = entity.reorderThreshold ?? 0;
  const avgOrdersValue = avgOrders ?? 0;

  // Calculate forecast period factor in terms of months
  const dateRangeMonths = TimePeriod[forecastPeriod]?.months || TimePeriod[DEFAULT_FORECAST_PERIOD].months;

  // Base required stock is average orders multiplied by date range months
  const baseRequiredStock = avgOrdersValue * dateRangeMonths;

  // Basic forecasting calculation
  let suggestedAmount =
    stock < reorderThreshold
      ? Math.round(baseRequiredStock * 1.2 + (reorderThreshold - stock) - stock) // If below threshold, order more aggressively
      : Math.round(baseRequiredStock * 1.1 - stock); // If above threshold, focus more on the average orders

  // Store the original value before applying modifiers
  const originalSuggestedAmount = Math.max(0, suggestedAmount);

  // Apply modifiers
  // Apply sales trend modifier (using either salesTrend or salesTrendModifier for backward compatibility)
  const salesTrendValue = modifiers.salesTrend !== undefined ? modifiers.salesTrend : modifiers.salesTrendModifier;

  if (salesTrendValue !== undefined) {
    suggestedAmount = Math.round(suggestedAmount * (1 + salesTrendValue / 100));
  }

  // Apply lead time adjustment
  if (modifiers.leadTimeDays) {
    // Default lead time is 7 days, adjust based on difference
    const defaultLeadTimeDays = 7;
    const leadTimeFactor = 1 + ((modifiers.leadTimeDays - defaultLeadTimeDays) / 30) * 0.1;
    suggestedAmount = Math.round(suggestedAmount * leadTimeFactor);
  }

  // Apply buffer stock
  if (modifiers.bufferStockDays) {
    // Calculate daily average orders
    const dailyAvgOrders = avgOrdersValue / 30; // Assuming 30 days in a month
    // Add buffer stock based on daily average and buffer days
    const bufferStockAmount = dailyAvgOrders * modifiers.bufferStockDays;
    suggestedAmount = Math.round(suggestedAmount + bufferStockAmount);
  }

  return {
    originalSuggestedAmount,
    modifiedSuggestedAmount: Math.max(0, suggestedAmount),
  };
};
